import PropTypes from 'prop-types';
import React from 'react';
import { FormGroup } from 'reactstrap';
import { Dropdown } from '@ami/react/core';
import {
  ImageMosaic,
  ImageMosaicDto,
  MosaicWall,
  TextMosaic,
  TextMosaicDto,
} from '@ami/react/core';
import {
  formatPrice,
  formatPriceWithoutCents,
} from '../../quotes/priceFormatter';
import { fns, OptionSetInfo } from './optionSetInfo';
import { FormToggle } from '@ami/react/core';
import BuildToOrderLeadTimeLabel from '../../quotes/lead-times/BuildToOrderLeadTimeLabel';
import { ValidableFeedback } from '@ami/react/core';
//import './ProductOptionSetComponent.scss';
import { ProductOptionSetInventorySettings } from '../../products/productOptionSet';

export default class ProductOptionSetComponent extends React.Component {
  static propTypes = {
    selectedProduct: PropTypes.instanceOf(Object).isRequired,
    productGroup: PropTypes.instanceOf(Object).isRequired,
    setInfo: PropTypes.instanceOf(OptionSetInfo).isRequired,
    onProductOptionSelected: PropTypes.func.isRequired,
    selectedProductOptionId: PropTypes.string,
    defaultSeriesId: PropTypes.string,
    index: PropTypes.number,
    originalProductOption: PropTypes.shape({
      productOptionSetId: PropTypes.string.isRequired,
      productOptionId: PropTypes.string.isRequired,
      step3Price: PropTypes.number.isRequired,
      step3Msrp: PropTypes.number.isRequired,
    }),
    isInventoryMode: PropTypes.bool.isRequired,
    optionSetInventorySetting: PropTypes.oneOf(
      Object.keys(ProductOptionSetInventorySettings).map(
        (key) => ProductOptionSetInventorySettings[key].id
      )
    ),
    hideLeadTimeAndPrices: PropTypes.bool.isRequired,
  };

  state = {
    selectedSeriesId: this.props.defaultSeriesId,
    showOptions: this.initializeOnOffToggle(),
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.defaultSeriesId !== this.props.defaultSeriesId) {
      this.setState({ selectedSeriesId: this.props.defaultSeriesId });
    }
  }

  markDefaultOptionAsSelected() {
    if (!this.state.showOptions) return;
    const series = this.props.setInfo.getSeries(this.state.selectedSeriesId);

    var selectedAnOption = false;
    // First, look for the user-defined default option and try to select that
    if (
      series &&
      series.defaultProductOptionId &&
      series.defaultProductOptionId !== this.props.selectedProductOptionId
    ) {
      var seriesProductOption = series.productOptions.find(
        (x) => x.id === series.defaultProductOptionId
      );
      if (seriesProductOption && seriesProductOption.available) {
        selectedAnOption = true;
        this.props.onProductOptionSelected(
          series.defaultProductOptionId,
          this.props.index
        );
      }
    }

    // Second, select the option automatically if there is only one visible to the user
    if (!selectedAnOption) {
      const availableOptionIds =
        this.props.setInfo.getAvailableProductOptionIds();
      if (availableOptionIds.length === 1) {
        selectedAnOption = true;
        this.props.onProductOptionSelected(
          availableOptionIds[0],
          this.props.index
        );
      }
    }

    // Lastly, do not select anything (user must select)
    if (!selectedAnOption) {
      this.props.onProductOptionSelected('', this.props.index);
    }
  }

  initializeOnOffToggle() {
    const setInfo = this.props.setInfo;
    return (
      setInfo.optionSet.isSelectionRequired ||
      !!this.props.selectedProductOptionId
    );
  }

  toggle(checked) {
    this.setState({ showOptions: checked }, () => {
      if (!checked) {
        this.props.onProductOptionSelected('', this.props.index);
      } else {
        this.markDefaultOptionAsSelected();
      }
    });
  }

  renderProductOptionsBySeriesSelector = () => {
    const { optionSetInventorySetting, setInfo } = this.props;
    const seriesOptions = fns.series.toFormSelectOption(
      setInfo.optionSet.productOptionsBySeries
    );
    if (
      optionSetInventorySetting ===
        ProductOptionSetInventorySettings.seriesAffectsInventory.id ||
      optionSetInventorySetting ===
        ProductOptionSetInventorySettings.seriesAndOptionAffectsInventory.id
    ) {
      const seriesOption = seriesOptions.find(
        (x) => x.id === this.state.selectedSeriesId
      );
      return seriesOption ? (
        <div className="mb-2" style={{ fontSize: '1rem' }}>
          {seriesOption.label}
        </div>
      ) : null;
    }
    return (
      <div className="optionset-series">
        <Dropdown
          items={seriesOptions}
          selectedId={this.state.selectedSeriesId}
          onChange={(selected) => {
            this.setState(
              {
                selectedSeriesId: selected,
              },
              () => this.markDefaultOptionAsSelected()
            );
          }}
        />
      </div>
    );
  };

  formatProductOptionDescription = (msrp, dnet, leadTime) => {
    return (
      <div className="row no-gutters mt-2">
        <div className="col-6 col-md-4">
          <div className="small">LEAD TIME</div>
          <div>
            <BuildToOrderLeadTimeLabel leadTimeDays={leadTime ? leadTime.numberOfDays : undefined} />
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="small">MSRP</div>
          <div>
            {msrp === 0
              ? '--'
              : msrp < 1000
              ? formatPrice(msrp)
              : formatPriceWithoutCents(msrp)}
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="small">D. NET</div>
          <div>
            {dnet === 0
              ? '--'
              : dnet < 1000
              ? formatPrice(dnet)
              : formatPriceWithoutCents(dnet)}
          </div>
        </div>
      </div>
    );
  };

  renderProductOptions = (seriesId) => {
    const {
      selectedProduct,
      setInfo,
      originalProductOption,
      selectedProductOptionId,
      onProductOptionSelected,
      index,
      isInventoryMode,
      optionSetInventorySetting,
      hideLeadTimeAndPrices,
    } = this.props;

    const ishideLeadTimeAndPrices =
      isInventoryMode ||
      hideLeadTimeAndPrices ||
      !setInfo.optionSet.showPriceAndLeadTime;
    const group = setInfo.optionSet.productOptionsBySeries.find(
      (x) => x.seriesId === seriesId
    );

    const productOptionsWithPrices = group
      ? fns.productOption.calculateAndAttachPrice(
          group.productOptions,
          setInfo.quantity,
          originalProductOption
        )
      : [];

    const getLeadTime = (selectedProduct, option) => {
      if (option.type === 'AMICustomization') {
        return option.supportsFastTrackLeadTimes && selectedProduct.leadTime.fastTrack ? selectedProduct.leadTime.fastTrack : selectedProduct.leadTime.standard;
      } else {
        return option.leadTime;
      }
    };

    return (
      <MosaicWall>
        {productOptionsWithPrices.map((option) => {
          if (
            optionSetInventorySetting ===
              ProductOptionSetInventorySettings.seriesAndOptionAffectsInventory
                .id &&
            option.id !== selectedProductOptionId
          ) {
            return null;
          }

          if (!option.available) {
            return null;
          }

          const leadTimeAndPrice = ishideLeadTimeAndPrices
            ? null
            : this.formatProductOptionDescription(
                option.step3Msrp,
                option.step3Price,
                getLeadTime(selectedProduct, option)
              );
          if (option.imageUrl) {
            const mosaicDto = new ImageMosaicDto({
              id: option.id,
              label: option.name,
              description: option.description,
              extraInformation: leadTimeAndPrice,
              imageUrl: option.imageUrl,
              selected: option.id === selectedProductOptionId,
            });
            return (
              <ImageMosaic
                key={option.id}
                data={mosaicDto}
                onClick={(productOptionId) =>
                  onProductOptionSelected(productOptionId, index)
                }
                mdWidthColumns={3}
              />
            );
          } else {
            const mosaicDto = new TextMosaicDto({
              id: option.id,
              label: option.name,
              description: option.description,
              extraInformation: leadTimeAndPrice,
              selected: option.id === selectedProductOptionId,
            });
            return (
              <TextMosaic
                key={option.id}
                data={mosaicDto}
                onClick={(productOptionId) =>
                  onProductOptionSelected(productOptionId, index)
                }
                widthColumns={4}
                mdWidthColumns={3}
              />
            );
          }
        })}
      </MosaicWall>
    );
  };

  render() {
    const setInfo = this.props.setInfo;
    const invalid =
      this.state.showOptions &&
      !this.props.selectedProductOptionId &&
      setInfo.hasAvailableOptions;

    if (!setInfo.hasAvailableOptions && !setInfo.optionSet.isAlwaysVisible) {
      return null;
    }

    return (
      <FormGroup className={`option-set-form ${invalid && 'invalid'}`}>
        <div className="row">
          <div className="col-9 col-sm-10">
            <div className="optionset-name">{setInfo.optionSet.name}</div>
            <ValidableFeedback
              invalid={invalid}
              text="Please enter a selection"
            />
            <div className="optionset-description">
              {setInfo.optionSet.description}
            </div>
          </div>
          <div className="col-3 col-sm-2 d-flex justify-content-end">
            {setInfo.hasAvailableOptions &&
              !setInfo.optionSet.isSelectionRequired && (
                <FormToggle
                  checked={this.state.showOptions}
                  label=""
                  onChange={(checked) => this.toggle(checked)}
                />
              )}
          </div>
        </div>
        {!setInfo.hasAvailableOptions && (
          <div className="text-warning optionset-notavailable">
            Not available based on other selections
          </div>
        )}
        {setInfo.hasAvailableOptions && this.state.showOptions && (
          <div className="mt-3">
            {setInfo.isSingleSeries() && (
              <React.Fragment>
                {this.renderProductOptions(
                  setInfo.optionSet.productOptionsBySeries[0].seriesId
                )}
              </React.Fragment>
            )}
            {!setInfo.isSingleSeries() && (
              <React.Fragment>
                {this.renderProductOptionsBySeriesSelector()}
                {this.renderProductOptions(this.state.selectedSeriesId)}
              </React.Fragment>
            )}
          </div>
        )}
      </FormGroup>
    );
  }
}
